"use client";
import Icon from "@/components/Icons/Icon";
import IconPlus from "@/components/Icons/IconPlus";
import { createFolderS3Urls } from "@/lib/api/general/create-s3-url";
import { MediaUploadDTO, FileInput } from "@alba-cars/common-modules";
import clsx from "clsx";
import { Loader2, Trash, RefreshCw, AlertCircle } from "lucide-react";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Control, Controller, Path, FieldValues } from "react-hook-form";

interface ImageUploaderProps<T extends FieldValues> {
  name: Path<T>;
  label?: string;
  maxImages?: number;
  control: Control<T>;
  required?: boolean;
  maxSizeMB?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

interface ImageDimensions {
  width: number;
  height: number;
}

interface UploadedImage {
  url: string;
  id: string;
  status: "uploading" | "failed" | "success";
  localUrl?: string;
  file?: File;
  progress?: number;
  error?: string;
  dimensions?: ImageDimensions;
}

type DimensionValidationResult = {
  width: number;
  height: number;
  valid: boolean;
  error?: string;
};

const getPresignedUrl = async (fileType: string): Promise<MediaUploadDTO> => {
  try {
    console.log("Requesting presigned URL for file type:", fileType);
    const response = await createFolderS3Urls("sell_car_requests", [
      { fileName: crypto.randomUUID() + "." + fileType.split("/")[1], fileType: fileType },
    ]);

    if (!response?.length) {
      console.error("Failed to get presigned URL: Empty response");
      throw new Error("Failed to get presigned url");
    }

    console.log("Received presigned URL response:", response[0]);
    return response[0];
  } catch (error) {
    console.error("Error getting presigned URL:", error);
    throw new Error("Failed to get presigned URL: " + (error instanceof Error ? error.message : String(error)));
  }
};

// Utility functions for image validation
const validateImageDimensions = (
  file: File,
  minWidth?: number,
  minHeight?: number,
  maxWidth?: number,
  maxHeight?: number,
): Promise<DimensionValidationResult> => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const width = img.width;
      const height = img.height;

      console.log(`Image dimensions: ${width}x${height}`);

      if (minWidth && width < minWidth) {
        resolve({ width, height, valid: false, error: `Image width must be at least ${minWidth}px` });
      }
      if (minHeight && height < minHeight) {
        resolve({ width, height, valid: false, error: `Image height must be at least ${minHeight}px` });
      }
      if (maxWidth && width > maxWidth) {
        resolve({ width, height, valid: false, error: `Image width must not exceed ${maxWidth}px` });
      }
      if (maxHeight && height > maxHeight) {
        resolve({ width, height, valid: false, error: `Image height must not exceed ${maxHeight}px` });
      }

      resolve({ width, height, valid: true });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      console.error("Failed to load image for dimension validation");
      resolve({ width: 0, height: 0, valid: false, error: "Failed to load image" });
    };

    img.src = objectUrl;
  });
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

interface DimensionConstraints {
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

// Define a custom type for upload progress tracking
type UploadProgress = {
  [key: number]: number;
};

interface SingleImageUploaderProps {
  buttonId: string;
  image?: UploadedImage;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  onRetry: () => void;
  maxSizeMB?: number;
  constraints?: DimensionConstraints;
  disabled?: boolean;
  progressValue?: number; // New prop for real-time progress
}

// Enhanced SingleImageUploader component
const SingleImageUploader: React.FC<SingleImageUploaderProps> = ({
  buttonId,
  image,
  onUpload,
  onRemove,
  onRetry,
  maxSizeMB = 5,
  constraints,
  disabled = false,
  progressValue,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get the actual progress value to display
  const displayProgress = progressValue !== undefined ? progressValue : image?.progress || 0;

  const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    onRemove();
  };

  const handleRetryClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    onRetry();

    // Clear the file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      <label
        htmlFor={`image-upload-${buttonId}`}
        className={clsx("cursor-pointer", disabled && "cursor-not-allowed opacity-60")}
      >
        {image?.localUrl || image?.url ? (
          <div className="relative">
            <img
              src={image.localUrl || image.url}
              alt="Uploaded"
              className={clsx("h-40 w-full rounded-2xl object-cover", image.status === "uploading" && "opacity-50")}
            />
            {/* Status overlays */}
            {image.status === "uploading" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-black/30">
                <Loader2 className="mb-2 h-8 w-8 animate-spin text-white" />
                <div className="w-3/4">
                  <div className="h-1.5 w-full rounded-full bg-white/30">
                    <div
                      className="h-full rounded-full bg-white transition-all duration-300"
                      style={{ width: `${displayProgress}%` }}
                    />
                  </div>
                  <p className="mt-1 text-center text-sm text-white">{displayProgress}%</p>
                </div>
              </div>
            )}
            {image.status === "failed" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl bg-black/50 p-4 text-center">
                <AlertCircle className="mb-2 h-6 w-6 text-red-400" />
                <p className="mb-2 text-xs text-white">{image.error || "Upload Failed"}</p>
                <button
                  onClick={handleRetryClick}
                  className="flex items-center space-x-1 rounded-lg bg-white px-3 py-1 text-sm hover:bg-gray-100"
                  disabled={disabled}
                >
                  <RefreshCw size={14} />
                  <span>Retry</span>
                </button>
              </div>
            )}
            {image.status === "success" && (
              <>
                <button onClick={handleRemoveClick} className="absolute right-2 top-2" disabled={disabled}>
                  <Icon icon={<Trash size={15} />} backgroundColor="white" className="h-8 w-8" />
                </button>
                {image.dimensions && (
                  <div className="absolute bottom-2 right-2 rounded-lg bg-black/50 px-2 py-1 text-xs text-white">
                    {image.dimensions.width} x {image.dimensions.height}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="flex h-40 w-full flex-col rounded-2xl bg-secondary-light py-6">
            <div className="flex flex-1 items-center justify-center">
              <IconPlus />
            </div>
            <span className="text-center font-inter text-sm font-medium text-black">Upload</span>
            <span className="mt-1 text-center text-xs text-gray-500">
              Max {maxSizeMB}MB
              {constraints && (
                <>
                  <br />
                  {constraints.minWidth &&
                    constraints.minHeight &&
                    `Min ${constraints.minWidth}x${constraints.minHeight}px`}
                  {constraints.maxWidth &&
                    constraints.maxHeight &&
                    ` Max ${constraints.maxWidth}x${constraints.maxHeight}px`}
                </>
              )}
            </span>
          </div>
        )}
      </label>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onUpload}
        style={{ display: "none" }}
        id={`image-upload-${buttonId}`}
        disabled={disabled}
      />
    </div>
  );
};

// Enhanced ImageUploader component with fixes for progress tracking
function ImageUploader<T extends FieldValues>({
  name,
  control,
  required = false,
  label,
  maxImages = 6,
  maxSizeMB = 5,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
}: ImageUploaderProps<T>): React.ReactElement {
  // State for real-time progress tracking - separate from form values
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});

  // Track ongoing uploads with a Map of index -> XHR for cancelation capability
  const [uploadXHRs, setUploadXHRs] = useState<Map<number, XMLHttpRequest>>(new Map());

  // Use useRef to safely access the current XHRs map during async operations
  const uploadXHRsRef = useRef<Map<number, XMLHttpRequest>>(new Map());

  // Update the ref whenever the state changes
  useEffect(() => {
    uploadXHRsRef.current = uploadXHRs;
  }, [uploadXHRs]);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? "This field is required" : false,
        validate: (value: UploadedImage[] | undefined) => {
          const images = value || [];

          // Filter out undefined values
          const validImages = images.filter((img) => img !== undefined);

          if (required && (!validImages.length || !validImages.some((img) => img?.status === "success"))) {
            return "Please upload at least one image successfully";
          }
          if (validImages.some((img) => img?.status === "uploading")) {
            return "Please wait for all images to finish uploading";
          }
          return true;
        },
      }}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        // Ensure value is always an array of UploadedImage | undefined
        const safeValue: (UploadedImage | undefined)[] = Array.isArray(value) ? value : [];

        const handleImageUpload = (index: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
          const files = e.target.files;
          const file = files?.[0];
          if (!file) return;

          console.log(`Starting upload for file: ${file.name}, size: ${formatBytes(file.size)}, type: ${file.type}`);

          // Cancel any existing upload at this index
          if (uploadXHRsRef.current.has(index)) {
            const xhr = uploadXHRsRef.current.get(index);
            if (xhr) {
              console.log(`Canceling previous upload at index ${index}`);
              xhr.abort();
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));
            }
          }

          // Generate a unique ID for this upload
          const uniqueId = crypto.randomUUID();
          console.log(`Generated unique ID for upload: ${uniqueId}`);

          // Reset progress for this index
          setUploadProgress((prev) => ({
            ...prev,
            [index]: 0,
          }));

          // Validate file size
          if (file.size > maxSizeMB * 1024 * 1024) {
            console.log(`File too large: ${formatBytes(file.size)}, max allowed: ${maxSizeMB}MB`);

            // Create a new array with the updated value
            const newImages = [...safeValue];
            newImages[index] = {
              url: "",
              id: uniqueId,
              status: "failed",
              error: `File size exceeds ${maxSizeMB}MB limit (${formatBytes(file.size)})`,
            };

            // Pass the new array directly to onChange
            onChange(newImages);
            return;
          }

          // Create local preview
          const localUrl = URL.createObjectURL(file);
          console.log(`Created local preview URL: ${localUrl}`);

          // Create a new array with the uploading state
          const newImages = [...safeValue];
          newImages[index] = {
            url: "",
            id: uniqueId,
            status: "uploading",
            localUrl,
            file,
            progress: 0, // Start at 0 progress
          };

          // Pass the new array directly to onChange
          onChange(newImages);

          try {
            // Validate dimensions
            console.log("Validating image dimensions...");
            const dimensionCheck = await validateImageDimensions(file, minWidth, minHeight, maxWidth, maxHeight);
            console.log("Dimension validation result:", dimensionCheck);

            if (!dimensionCheck.valid) {
              console.log(`Dimension validation failed: ${dimensionCheck.error}`);

              // Create a new array with the error state
              const newImages = [...safeValue];

              // Only update if this is still our upload
              newImages[index] = {
                url: "",
                id: uniqueId,
                status: "failed",
                localUrl,
                error: dimensionCheck.error || "Image dimensions not valid",
                file,
              };

              // Pass the new array directly to onChange
              onChange(newImages);
              return;
            }

            // Get presigned URL for S3 upload
            console.log("Getting presigned URL...");
            const { uploadUrl, mediaKey: imageId } = await getPresignedUrl(file.type);
            console.log("Got presigned URL:", uploadUrl);
            console.log("Media key:", imageId);

            // Create and configure XHR for upload
            const xhr = new XMLHttpRequest();
            console.log("Created XHR for upload");

            // Store XHR reference to enable cancellation
            uploadXHRsRef.current.set(index, xhr);
            setUploadXHRs(new Map(uploadXHRsRef.current));

            // CRITICAL: Set up progress tracking correctly using our separate state
            xhr.upload.addEventListener("progress", (event) => {
              if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                console.log(`Progress event: ${event.loaded} / ${event.total}`);
                console.log(`Calculated progress: ${progress}%`);

                // Update our separate progress state first - this will update the UI immediately
                setUploadProgress((prev) => ({
                  ...prev,
                  [index]: progress,
                }));

                // Only update the form state occasionally to reduce re-renders
                if (progress % 20 === 0 || progress === 100) {
                  const formImages = [...safeValue];
                  if (formImages[index]?.id === uniqueId) {
                    formImages[index] = {
                      ...formImages[index]!,
                      progress: progress,
                    };
                    onChange(formImages);
                  }
                }
              }
            });

            // Add an event for upload start
            xhr.addEventListener("loadstart", () => {
              console.log("Upload started");
            });

            // Setup completion handler
            xhr.addEventListener("load", () => {
              console.log(`Upload completed with status: ${xhr.status}`);

              // Clean up XHR reference
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));

              // Ensure progress is at 100% in our separate state
              setUploadProgress((prev) => ({
                ...prev,
                [index]: 100,
              }));

              if (xhr.status >= 200 && xhr.status < 300) {
                // Extract clean URL from presigned URL (remove query parameters)
                const url = uploadUrl.split("?")[0];
                console.log("Final URL after removing query params:", url);

                // Set progress to 100% in form state
                const formImages = [...safeValue];

                // Update the form state with the success status
                formImages[index] = {
                  url,
                  id: imageId,
                  status: "success",
                  localUrl,
                  dimensions: {
                    width: dimensionCheck.width,
                    height: dimensionCheck.height,
                  },
                  file,
                  progress: 100,
                };

                onChange(formImages);
              } else {
                console.error(`Upload failed with HTTP status: ${xhr.status}`);

                // Update form state with error
                const formImages = [...safeValue];
                formImages[index] = {
                  url: "",
                  id: uniqueId,
                  status: "failed",
                  localUrl,
                  error: `Upload failed with status code: ${xhr.status}`,
                  file,
                };

                onChange(formImages);
              }
            });

            // Add timeout handler
            xhr.addEventListener("timeout", () => {
              console.error("Upload timed out");

              // Clean up XHR reference
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));

              // Update with error state
              const formImages = [...safeValue];
              formImages[index] = {
                url: "",
                id: uniqueId,
                status: "failed",
                localUrl,
                error: "Upload timed out",
                file,
              };

              onChange(formImages);
            });

            // Error handler
            xhr.addEventListener("error", (e) => {
              console.error("XHR network error:", e);

              // Clean up XHR reference
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));

              // Update with error state
              const formImages = [...safeValue];
              formImages[index] = {
                url: "",
                id: uniqueId,
                status: "failed",
                localUrl,
                error: "Network error during upload",
                file,
              };

              onChange(formImages);
            });

            // Abort handler
            xhr.addEventListener("abort", () => {
              console.log("Upload aborted");

              // Clean up XHR reference
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));
            });

            // Configure and send the request
            console.log("Opening XHR connection...");
            xhr.open("PUT", uploadUrl);

            // Set a reasonable timeout
            xhr.timeout = 120000; // 2 minutes

            // Set content type to match the file
            console.log(`Setting Content-Type header to: ${file.type}`);
            xhr.setRequestHeader("Content-Type", file.type);

            // Send the file
            console.log("Sending file...");
            xhr.send(file);
            console.log("Upload initiated");
          } catch (error) {
            console.error("Upload failed with exception:", error);

            // Update with error state
            const formImages = [...safeValue];
            formImages[index] = {
              url: "",
              id: uniqueId,
              status: "failed",
              localUrl,
              error: error instanceof Error ? error.message : "Upload failed",
              file,
            };

            onChange(formImages);

            // Clean up any XHR reference
            if (uploadXHRsRef.current.has(index)) {
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));
            }
          }
        };

        const handleRemoveImage = (index: number): void => {
          console.log(`Removing image at index ${index}`);

          // Cancel any active upload
          if (uploadXHRsRef.current.has(index)) {
            const xhr = uploadXHRsRef.current.get(index);
            if (xhr) {
              console.log("Aborting active upload");
              xhr.abort();
              uploadXHRsRef.current.delete(index);
              setUploadXHRs(new Map(uploadXHRsRef.current));
            }
          }

          // Get the current image to clean up resources
          const currentImage = safeValue[index];

          // Clean up object URL to prevent memory leaks
          if (currentImage?.localUrl) {
            console.log("Revoking object URL");
            URL.revokeObjectURL(currentImage.localUrl);
          }

          // Create a new array with the value set to undefined
          const newImages = [...safeValue];
          newImages[index] = undefined;

          // Pass the new array directly to onChange
          onChange(newImages);

          // Clear progress for this index
          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[index];
            return newProgress;
          });

          // Reset the file input to allow re-uploading the same file
          const fileInput = document.getElementById(`image-upload-${index}`) as HTMLInputElement;
          if (fileInput) {
            fileInput.value = "";
          }
        };

        const handleRetry = (index: number): (() => Promise<void>) => {
          return async () => {
            console.log(`Retrying upload at index ${index}`);

            // Cancel any active upload
            if (uploadXHRsRef.current.has(index)) {
              console.log("An upload is already in progress");
              return;
            }

            const currentImage = safeValue[index];

            if (!currentImage?.file) {
              console.warn("No file available for retry");
              return;
            }

            // Use the existing file for retry
            const fakeEvent = {
              target: {
                files: [currentImage.file],
              },
            } as unknown as React.ChangeEvent<HTMLInputElement>;

            await handleImageUpload(index)(fakeEvent);
          };
        };

        // Ensure we have correct amount of placeholders
        const displayItems: (UploadedImage | undefined)[] = [...safeValue];
        while (displayItems.length < maxImages) {
          displayItems.push(undefined);
        }

        // Check if uploads are in progress
        const isUploading = uploadXHRs.size > 0;

        return (
          <div className="mt-10">
            {label && <h3 className="font-inter text-base font-bold text-primary-foreground">{label}</h3>}
            <div className="mt-4 grid w-full grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
              {displayItems.slice(0, maxImages).map((img, index) => (
                <div
                  key={index}
                  className={clsx(
                    index < 2 ? "w-full" : index === 2 ? "col-span-2 mb-5 lg:col-span-1" : "hidden lg:block",
                    "lg:w-full",
                  )}
                >
                  <SingleImageUploader
                    buttonId={String(index)}
                    image={img}
                    onUpload={handleImageUpload(index)}
                    onRemove={() => handleRemoveImage(index)}
                    onRetry={handleRetry(index)}
                    maxSizeMB={maxSizeMB}
                    constraints={{
                      minWidth,
                      minHeight,
                      maxWidth,
                      maxHeight,
                    }}
                    disabled={isUploading && img?.status !== "uploading"}
                    progressValue={uploadProgress[index]}
                  />
                </div>
              ))}
            </div>
            {error && <p className="mt-1 text-sm text-red-500">{error.message}</p>}
          </div>
        );
      }}
    />
  );
}

export default ImageUploader;
